import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Button, Input, Spinner, Modal } from "../components/ui";
import { WorkStatusPill, ProgressBar, QuadrantBadge } from "../components/work";
import GoalFormModal from "../components/GoalFormModal";
import ProjectFormModal from "../components/ProjectFormModal";
import { ArrowLeft, Plus, Target, FolderKanban, Pencil, Trash2, Check, X, Calendar, User } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function GoalPage() {
  const { key, spaceId, goalId } = useParams();
  const navigate = useNavigate();
  const { t, lang, entities } = useApp();
  const entity = entities.find((e) => e.key === key);
  const color = entity?.color || "#2563EB";

  const [goal, setGoal] = useState(null);
  const [subgoals, setSubgoals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const [showProject, setShowProject] = useState(false);

  // inline add forms
  const [tForm, setTForm] = useState(null);
  const [cText, setCText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, subs, projs, o, kr] = await Promise.all([
        api.get(`/goals/${goalId}`),
        api.get(`/goals?space_id=${spaceId}`),
        api.get(`/space-projects?space_id=${spaceId}`),
        api.get("/data/objectives"),
        api.get("/data/key_results"),
      ]);
      setGoal(g.data);
      setSubgoals(subs.data.filter((x) => x.parent_goal_id === goalId));
      setProjects(projs.data.filter((x) => x.parent_goal_id === goalId));
      const objTitle = Object.fromEntries(o.data.map((x) => [x.id, x.title]));
      setKeyResults(kr.data.map((k) => ({ ...k, objective_title: objTitle[k.objective_id] })));
    } finally { setLoading(false); }
  }, [goalId, spaceId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => { await api.put(`/goals/${goalId}`, fields); await load(); };

  const addTarget = async () => {
    if (!tForm?.name?.trim()) return;
    const targets = [...(goal.targets || []), {
      id: uid(), name: tForm.name.trim(),
      start: Number(tForm.start) || 0, target: Number(tForm.target) || 0,
      current: Number(tForm.current) || 0, unit: tForm.unit || "",
    }];
    setTForm(null);
    await patch({ targets });
  };
  const removeTarget = async (id) => patch({ targets: (goal.targets || []).filter((x) => x.id !== id) });

  const addCheck = async () => {
    if (!cText.trim()) return;
    const checklist = [...(goal.checklist || []), { id: uid(), text: cText.trim(), done: false }];
    setCText("");
    await patch({ checklist });
  };
  const toggleCheck = async (id) => patch({
    checklist: (goal.checklist || []).map((c) => c.id === id ? { ...c, done: !c.done } : c),
  });
  const removeCheck = async (id) => patch({ checklist: (goal.checklist || []).filter((c) => c.id !== id) });

  const deleteGoal = async () => {
    if (!window.confirm(lang === "fr" ? "Supprimer cet objectif ?" : "Delete this goal?")) return;
    await api.delete(`/goals/${goalId}`);
    navigate(`/entity/${key}/space/${spaceId}`);
  };

  if (!entity) return <div className="p-8 text-text-secondary">…</div>;
  if (loading || !goal) return <div className="py-24 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;

  const linkedKR = keyResults.find((k) => k.id === goal.key_result_id);
  // Subgoals go back to their parent goal; root goals go back to the space.
  const backPath = goal.parent_goal_id
    ? `/entity/${key}/space/${spaceId}/goal/${goal.parent_goal_id}`
    : `/entity/${key}/space/${spaceId}`;

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto" data-testid="goal-page">
      <button onClick={() => navigate(backPath)} className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors" data-testid="goal-back">
        <ArrowLeft className="w-4 h-4" /> {t("back")}
      </button>

      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <Target className="w-6 h-6" style={{ color }} />
          <h1 className="text-3xl font-heading font-medium tracking-tight">{goal.name}</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowEdit(true)} data-testid="goal-edit-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /> {t("edit")}</button>
          <button onClick={deleteGoal} data-testid="goal-delete-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-red-400 hover:bg-red-500/15 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {goal.description && <p className="text-text-secondary text-sm mb-6">{goal.description}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* main */}
        <div className="lg:col-span-2 space-y-7">
          {/* Targets */}
          <section data-testid="goal-targets">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-medium">{t("targets")}</h3>
              {!tForm && <Button size="sm" variant="ghost" onClick={() => setTForm({})} data-testid="add-target-button"><Plus className="w-3.5 h-3.5" /> {t("add_target")}</Button>}
            </div>
            <div className="space-y-3">
              {(goal.targets || []).map((tg) => {
                const pct = tg.target !== tg.start ? Math.max(0, Math.min(100, Math.round((tg.current - tg.start) / (tg.target - tg.start) * 100))) : 0;
                return (
                  <Card key={tg.id} className="p-4" data-testid={`target-${tg.id}`}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-medium">{tg.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-text-secondary tabular-nums">{tg.current} / {tg.target} {tg.unit}</span>
                        <button onClick={() => removeTarget(tg.id)} data-testid={`target-del-${tg.id}`} className="text-text-secondary hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <ProgressBar value={pct} color={color} />
                  </Card>
                );
              })}
              {(goal.targets || []).length === 0 && !tForm && <p className="text-sm text-text-secondary">{t("no_targets")}</p>}
              {tForm && (
                <Card className="p-4 space-y-2" data-testid="target-form">
                  <Input placeholder={t("target_name")} value={tForm.name || ""} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="target-name" autoFocus />
                  <div className="grid grid-cols-4 gap-2">
                    <Input type="number" placeholder={t("start")} value={tForm.start ?? ""} onChange={(e) => setTForm({ ...tForm, start: e.target.value })} data-testid="target-start" />
                    <Input type="number" placeholder={t("target_value")} value={tForm.target ?? ""} onChange={(e) => setTForm({ ...tForm, target: e.target.value })} data-testid="target-target" />
                    <Input type="number" placeholder={t("current")} value={tForm.current ?? ""} onChange={(e) => setTForm({ ...tForm, current: e.target.value })} data-testid="target-current" />
                    <Input placeholder={t("unit")} value={tForm.unit || ""} onChange={(e) => setTForm({ ...tForm, unit: e.target.value })} data-testid="target-unit" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setTForm(null)}>{t("cancel")}</Button>
                    <Button size="sm" onClick={addTarget} data-testid="target-add">{t("add")}</Button>
                  </div>
                </Card>
              )}
            </div>
          </section>

          {/* Checklist */}
          <section data-testid="goal-checklist">
            <h3 className="font-heading font-medium mb-3">{t("checklist")}</h3>
            <div className="space-y-1.5">
              {(goal.checklist || []).map((c) => (
                <div key={c.id} className="flex items-center gap-2.5 group" data-testid={`check-${c.id}`}>
                  <button onClick={() => toggleCheck(c.id)} data-testid={`check-toggle-${c.id}`}
                    className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors"
                    style={c.done ? { backgroundColor: color, borderColor: color } : { borderColor: "var(--border-strong)" }}>
                    {c.done && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <span className={`text-sm flex-1 ${c.done ? "line-through text-text-secondary" : ""}`}>{c.text}</span>
                  <button onClick={() => removeCheck(c.id)} className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-red-400 transition-all"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Input placeholder={t("add_check_placeholder")} value={cText} onChange={(e) => setCText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCheck()} data-testid="check-input" />
              <Button size="sm" variant="outline" onClick={addCheck} data-testid="check-add"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
          </section>

          {/* Subgoals & Projects */}
          <section data-testid="goal-children">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-medium">{t("subgoals_projects")}</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShowSub(true)} data-testid="add-subgoal-button"><Plus className="w-3.5 h-3.5" /> {t("goal")}</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowProject(true)} data-testid="add-child-project-button"><Plus className="w-3.5 h-3.5" /> {t("project")}</Button>
              </div>
            </div>
            <div className="space-y-2">
              {[
                ...subgoals.map((g) => ({ ...g, _kind: "goal" })),
                ...projects.map((p) => ({ ...p, _kind: "project" })),
              ].map((it) => (
                <button key={`${it._kind}-${it.id}`}
                  onClick={() => navigate(`/entity/${key}/space/${spaceId}/${it._kind}/${it.id}`)}
                  data-testid={`${it._kind === "goal" ? "subgoal" : "child-project"}-${it.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-surface hover:bg-surface-hover transition-colors text-left">
                  {it._kind === "goal"
                    ? <Target className="w-4 h-4 shrink-0" style={{ color }} />
                    : <FolderKanban className="w-4 h-4 shrink-0" style={{ color }} />}
                  <span className="font-medium text-sm flex-1 truncate">{it.name}</span>
                  <WorkStatusPill status={it.status} lang={lang} />
                </button>
              ))}
              {subgoals.length === 0 && projects.length === 0 && <p className="text-sm text-text-secondary">{t("no_subitems")}</p>}
            </div>
          </section>
        </div>

        {/* sidebar */}
        <aside className="space-y-5">
          <Card className="p-5 space-y-4">
            <div>
              <div className="text-xs text-text-secondary mb-1.5">{t("status")}</div>
              <WorkStatusPill status={goal.status} lang={lang} />
            </div>
            <div>
              <div className="text-xs text-text-secondary mb-1.5">{t("priority")}</div>
              {goal.quadrant ? <QuadrantBadge quadrant={goal.quadrant} t={t} /> : <span className="text-sm text-text-secondary">—</span>}
            </div>
            <div>
              <div className="text-xs text-text-secondary mb-1.5">{t("progress")}</div>
              <ProgressBar value={goal.progress} color={color} />
            </div>
            <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-text-secondary" /><span className="text-text-secondary">{t("due_date")}:</span> {goal.due_date || "—"}</div>
            <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-text-secondary" /><span className="text-text-secondary">{t("champion")}:</span> {goal.champion || "—"}</div>
            {linkedKR && (
              <div>
                <div className="text-xs text-text-secondary mb-1.5">{t("linked_key_result")}</div>
                <span className="inline-flex items-start gap-1.5 text-sm" data-testid="goal-kr-link">
                  <Target className="w-3.5 h-3.5 text-text-secondary mt-0.5 shrink-0" />
                  <span>{linkedKR.objective_title && <span className="text-text-secondary">{linkedKR.objective_title} › </span>}{linkedKR.title}</span>
                </span>
              </div>
            )}
          </Card>
        </aside>
      </div>

      <GoalFormModal open={showEdit} onClose={() => setShowEdit(false)} onSaved={load} editing={goal} entityId={entity.id} spaceId={spaceId} keyResults={keyResults} />
      <GoalFormModal open={showSub} onClose={() => setShowSub(false)} onSaved={load} entityId={entity.id} spaceId={spaceId} parentGoalId={goalId} keyResults={keyResults} />
      <ProjectFormModal open={showProject} onClose={() => setShowProject(false)} onSaved={load} entityId={entity.id} spaceId={spaceId} parentGoalId={goalId} />
    </div>
  );
}
